import { useEffect, useId, useState } from 'react'
import { t } from './sprache'
import { alsPeriodenfeld, ausPeriodenfeld } from './Zeitraum'

/**
 * **Ein Feld für jede Periodeneingabe in der Anwendung** — MM.JJJJ (R502, R839).
 *
 * **Ein Bauteil, überall eingebettet** (R317). Vor dem 2026-09-13 stand an
 * sieben Stellen ein `<input type="number">` mit `202609` darin: im
 * Anlageblatt, im Gliederungsbaum, im Zuordnungstisch, im Datenimport, im
 * Eigenschaftsblatt, auf dem Anlageformular und beim Löschen. Sieben Felder
 * sind sieben Gelegenheiten, die Schreibweise verschieden zu ändern — und
 * jedes von ihnen verlangte vom Anwender, in der **Speicherform** zu denken.
 *
 * **Getippt wird als Text, gerechnet wird in `jjjjmm`.** Ein Zahlenfeld hätte
 * den Punkt gar nicht angenommen; ein Datumsfeld verlangte einen Tag, den es
 * hier nicht gibt. Der Wert reist als Zahl weiter, wie überall im Modell.
 *
 * **Eine Fehleingabe steht als Satz da, nicht als roter Rahmen** (R143, R145):
 * Der Rahmen sagt *falsch*, der Satz sagt *wie richtig* — er nennt die
 * Schreibweisen, die das Feld annimmt.
 *
 * **Das Feld nimmt mehrere Schreibweisen und nennt sie in seinem Hinweis**
 * (R839 — Eine Periode liest sich als Monat und Jahr; Entscheidung des
 * Auftraggebers vom 2026-09-30): `12.2026`, `12/2026`, `12-2026`, `2026-12`
 * und `202612`. Der Hinweis erscheint, wenn der Zeiger auf dem Feld ruht;
 * gelesen wird die Eingabe von `ausPeriodenfeld`. Es bleibt **ein** Feld für
 * jede Periodeneingabe — geöffnet wurde die Schreibweise, nicht die Zahl der
 * Bauteile.
 *
 * **Gemeldet wird erst, wenn etwas dasteht.** Ein leeres Feld ist keine
 * Fehleingabe, sondern ein Feld, in dem noch nichts steht; und während des
 * Tippens ist jede Zwischenstufe kurz unvollständig. Deshalb sagt das Feld
 * dem Aufrufer `null` und meldet erst, wenn der Anwender es verlassen hat.
 */
export function Periodenfeld({
  wert,
  aufWert,
  beschriftung,
  gesperrt = false,
  erstesFeld,
  klasse,
  erlaeuterung,
}: {
  /** Die geltende Periode als `jjjjmm` — `null` heißt: noch keine. */
  wert: number | null
  /** Der neue Wert; `null`, solange die Eingabe keine Periode ergibt. */
  aufWert: (periode: number | null) => void
  beschriftung: string
  gesperrt?: boolean
  erstesFeld?: React.Ref<HTMLInputElement>
  /**
   * Die Klasse der Umhüllung — Vorgabe `suchzeile`.
   *
   * **Leer, wo das Feld in einem Raster steht:** `.stammdatenfelder` setzt
   * seine Felder über `> label` und braucht keinen zweiten Rahmen darum.
   */
  klasse?: string
  /** Ein Satz unter dem Feld, wo einer dazugehört — wie an den Nachbarfeldern. */
  erlaeuterung?: string
}) {
  const [text, setText] = useState(() => alsPeriodenfeld(wert))
  const [beruehrt, setBeruehrt] = useState(false)
  const hilfeId = useId()

  // **Von außen gesetzt sticht.** Wechselt der Anwender die Kontextperiode,
  // während die Maske offen ist, steht dort die neue — aber nur, wenn er nicht
  // gerade selbst etwas anderes getippt hat: Das wäre ein Feld, das sich unter
  // den Fingern ändert.
  useEffect(() => {
    setText((bisher) => (ausPeriodenfeld(bisher) === wert ? bisher : alsPeriodenfeld(wert)))
  }, [wert])

  const gelesen = ausPeriodenfeld(text)
  const falsch = beruehrt && text.trim() !== '' && gelesen === null

  return (
    <label className={klasse ?? 'suchzeile'}>
      <span className="name">{beschriftung}</span>
      <input
        ref={erstesFeld}
        value={text}
        disabled={gesperrt}
        inputMode="numeric"
        placeholder={t('periode.muster')}
        title={t('periode.muster.hinweis')}
        // Wo das Raster die Beschriftung verbirgt (Klasse nurfeld in Tafel, Blatt
        // und Belegmaske), trägt das Feld seinen Namen selbst — sonst liest ein
        // Vorleseprogramm ein namenloses Eingabefeld (R839).
        aria-label={beschriftung}
        aria-describedby={falsch ? hilfeId : undefined}
        aria-invalid={falsch || undefined}
        onChange={(e) => {
          setText(e.target.value)
          aufWert(ausPeriodenfeld(e.target.value))
        }}
        onBlur={() => setBeruehrt(true)}
      />

      {/*
        **Der Hilfetext steht im Feld und nicht daneben** (R431): Er gehört zu
        dieser Eingabe, und in einem Raster aus drei Feldern nebeneinander wäre
        ein Satz außerhalb der Zelle einer, der zu keinem gehört.
      */}
      {falsch && (
        <span className="hinweis periodenhilfe" id={hilfeId}>
          {t('periode.ungueltig')}
        </span>
      )}

      {erlaeuterung && !falsch && <span className="erlaeuterung">{erlaeuterung}</span>}
    </label>
  )
}
